import { Controller } from "@hotwired/stimulus";
import * as Turbo from "@hotwired/turbo";
import {
  createMap,
  fitBounds,
  LOT_LABEL_STYLE,
} from "../../helpers/map_helpers.js";
import { serializeLayerGeometry } from "./geometry_serialization.js";
import "@geoman-io/leaflet-geoman-free";

export default class extends Controller {
  static targets = [
    "container",
    "polygonCoordinates",
    "drawOverlay",
    "geographicFileInput",
    "areaField",
  ];

  static EXISTING_LOT_STYLE = {
    color: "#3B82F6",
    weight: 2,
    opacity: 0.8,
    fillOpacity: 0.2,
  };

  static CURRENT_LOT_STYLE = {
    color: "#6B21A8",
    weight: 3,
    opacity: 1,
    fillOpacity: 0.3,
  };

  static CURRENT_LOT_LABEL_STYLE = `
    background: rgba(107, 33, 168, 0.9);
    color: white;
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 600;
    text-align: center;
    border: 1px solid rgba(107, 33, 168, 1);
    box-shadow: 0 2px 4px rgba(0,0,0,0.2);
    white-space: nowrap;
    pointer-events: none;
  `;

  connect() {
    this.initializeProperties();
    this.setupMap();
    this.setupEventListeners();
    this.renderExistingLots();
    this.renderCurrentLot();
    this.focusMap();
  }

  initializeProperties() {
    const {
      lat,
      lon,
      polygon,
      existingLots,
      currentLotId,
      newLotLabel,
      drawLabel,
    } = this.element.dataset;

    this.lat = parseFloat(lat);
    this.lon = parseFloat(lon);
    this.polygon = polygon ? JSON.parse(polygon) : null;
    this.existingLots = existingLots ? JSON.parse(existingLots) : [];
    this.currentLotId = currentLotId;
    this.newLotLabel = newLotLabel;
    this.drawLabel = drawLabel;

    // State management
    this.existingLotLayers = [];
    this.currentLotLayer = null;
    this.currentLotLabel = null;
    this.centerDrawButton = null;
    this.isInDrawMode = false;
    this.isProgrammaticRemoval = false;
  }

  setupMap() {
    this.map = createMap(this.containerTarget, {
      lat: this.lat,
      lon: this.lon,
    });

    // Initialize Geoman controls (editable — not read-only)
    this.map.pm.addControls({
      position: "topleft",
      editMode: true,
      removalMode: true,
      drawPolygon: false,
      drawMarker: false,
      drawCircleMarker: false,
      drawPolyline: false,
      drawCircle: false,
      drawRectangle: false,
      dragMode: false,
      cutPolygon: false,
      drawText: false,
    });
  }

  setupEventListeners() {
    // Geoman events
    this.map.on("pm:create", this.handlePolygonCreate.bind(this));
    this.map.on("pm:remove", this.handlePolygonRemove.bind(this));
    this.map.on("pm:dragstart", this.preventExistingLotModification.bind(this));
    this.map.on(
      "pm:markerdragstart",
      this.preventExistingLotModification.bind(this),
    );
    this.map.on("pm:editstart", this.preventExistingLotEdit.bind(this));

    // File upload event
    this.boundHandleFileUpload = this.handleFileUpload.bind(this);
    this.geographicFileInputTarget.addEventListener(
      "change",
      this.boundHandleFileUpload,
    );
  }

  disconnect() {
    this.geographicFileInputTarget?.removeEventListener(
      "change",
      this.boundHandleFileUpload,
    );
    this.map?.remove();
  }

  renderExistingLots() {
    this.existingLots.forEach((lot) => {
      if (lot.polygon) {
        const layer = this.createExistingLotLayer(lot);
        const label = this.createExistingLotLabel(lot, layer);
        this.existingLotLayers.push(layer, label);
      }
    });
  }

  renderCurrentLot() {
    if (this.polygon) {
      this.currentLotLayer = this.createCurrentLotLayer(this.polygon);
      this.addCurrentLotLabel();
    }
  }

  // Layer creation helper methods
  createExistingLotLayer(lot) {
    const layer = L.geoJSON(JSON.parse(lot.polygon), {
      style: this.constructor.EXISTING_LOT_STYLE,
      pmIgnore: true,
    }).addTo(this.map);

    this.markAsExistingLot(layer);
    layer.bindTooltip(lot.name);
    return layer;
  }

  createExistingLotLabel(lot, layer) {
    const center = layer.getBounds().getCenter();
    const label = L.marker(center, {
      icon: L.divIcon({
        className: "lot-label",
        html: `<div style="${LOT_LABEL_STYLE}">${lot.name}</div>`,
        iconSize: [null, null],
        iconAnchor: [null, null],
      }),
      pmIgnore: true,
      snapIgnore: true,
    }).addTo(this.map);

    label.isExistingLot = true;
    return label;
  }

  createCurrentLotLayer(polygon) {
    const layer = L.geoJSON(polygon, {
      style: this.constructor.CURRENT_LOT_STYLE,
      pmIgnore: false,
    }).addTo(this.map);

    this.markAsCurrentLot(layer);
    layer.on("pm:edit", () => {
      this.setPolygonCoordinates(this.currentLotLayer);
      this.addCurrentLotLabel();
      this.fetchAndSetArea();
    });

    return layer;
  }

  // Layer marking helper methods
  markAsExistingLot(layer) {
    layer.isExistingLot = true;

    layer.options.pmIgnore = true;
    if (layer.pm) {
      layer.pm.setOptions({ pmIgnore: true });
    }

    if (layer.eachLayer && typeof layer.eachLayer === "function") {
      layer.eachLayer((sublayer) => {
        sublayer.isExistingLot = true;
        sublayer.options.pmIgnore = true;
        if (sublayer.pm) {
          sublayer.pm.setOptions({ pmIgnore: true });
        }
      });
    }
  }

  markAsCurrentLot(layer) {
    layer.isExistingLot = false;

    layer.options.pmIgnore = false;
    if (layer.pm) {
      layer.pm.setOptions({
        pmIgnore: false,
        allowSelfIntersection: false,
      });
      layer.pm.enable();
    }

    if (layer.eachLayer && typeof layer.eachLayer === "function") {
      layer.eachLayer((sublayer) => {
        sublayer.isExistingLot = false;
        sublayer.options.pmIgnore = false;
        if (sublayer.pm) {
          sublayer.pm.setOptions({
            pmIgnore: false,
            allowSelfIntersection: false,
          });
          sublayer.pm.enable();
        }
      });
    }
  }

  // Drawing workflow methods
  enableDraw(event) {
    this.removeCurrentLot();
    this.drawOverlayTarget.classList.add("hidden");
    this.isInDrawMode = true;
    this.showCenterDrawButton();
  }

  showCenterDrawButton() {
    this.removeCenterDrawButton();
    this.centerDrawButton = this.createCenterDrawButton();
    this.containerTarget.style.position = "relative";
    this.containerTarget.appendChild(this.centerDrawButton);
  }

  createCenterDrawButton() {
    const button = document.createElement("div");
    button.className = "center-draw-button";
    button.style.cssText = `
      position: absolute;
      top: 10px;
      left: 50%;
      transform: translateX(-50%);
      z-index: 1000;
      pointer-events: auto;
    `;

    button.innerHTML = `
      <button style="
        background: #6B21A8;
        color: white;
        border: none;
        padding: 12px 24px;
        border-radius: 8px;
        font-size: 14px;
        font-weight: 600;
        cursor: pointer;
        box-shadow: 0 4px 12px rgba(0,0,0,0.3);
        white-space: nowrap;
      " class="center-draw-btn">
        ${this.drawLabel}
      </button>
    `;

    const btnElement = button.querySelector(".center-draw-btn");
    btnElement.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.actuallyEnableDraw();
    });

    return button;
  }

  actuallyEnableDraw() {
    this.removeCenterDrawButton();
    this.map.pm.enableDraw("Polygon", {
      snappable: true,
      snapDistance: 20,
      allowSelfIntersection: false,
    });
  }

  removeCenterDrawButton() {
    if (this.centerDrawButton) {
      this.centerDrawButton.remove();
      this.centerDrawButton = null;
    }
  }

  // Event protection methods
  preventExistingLotModification(e) {
    if (e.layer && e.layer.isExistingLot) {
      if (e.cancel && typeof e.cancel === "function") {
        e.cancel();
      }
      return false;
    }
  }

  preventExistingLotEdit(e) {
    if (e.layer && e.layer.isExistingLot) {
      if (e.cancel && typeof e.cancel === "function") {
        e.cancel();
      }
      return false;
    }
  }

  // Lot management methods
  removeCurrentLot() {
    this.isProgrammaticRemoval = true;

    if (this.currentLotLayer) {
      this.map.removeLayer(this.currentLotLayer);
      this.currentLotLayer = null;
    }
    if (this.currentLotLabel) {
      this.map.removeLayer(this.currentLotLabel);
      this.currentLotLabel = null;
    }

    this.isProgrammaticRemoval = false;
    this.removeCenterDrawButton();
  }

  addCurrentLotLabel() {
    if (this.isInDrawMode) {
      return;
    }

    if (this.currentLotLabel) {
      this.map.removeLayer(this.currentLotLabel);
    }

    if (this.currentLotLayer) {
      const center = this.currentLotLayer.getBounds().getCenter();
      this.currentLotLabel = L.marker(center, {
        icon: L.divIcon({
          className: "current-lot-label",
          html: `<div style="${this.constructor.CURRENT_LOT_LABEL_STYLE}">${this.newLotLabel}</div>`,
          iconSize: [null, null],
          iconAnchor: [null, null],
        }),
      }).addTo(this.map);
    }
  }

  focusMap() {
    const allLayers = [...this.existingLotLayers];
    if (this.currentLotLayer) {
      allLayers.push(this.currentLotLayer);
    }

    fitBounds(this.map, allLayers);
  }

  // Geoman event handlers
  handlePolygonCreate({ layer }) {
    this.removeCenterDrawButton();
    this.isInDrawMode = false;
    this.map.pm.disableDraw();

    layer.setStyle(this.constructor.CURRENT_LOT_STYLE);
    this.markAsCurrentLot(layer);

    layer.on("pm:edit", () => {
      this.setPolygonCoordinates(this.currentLotLayer);
      this.addCurrentLotLabel();
      this.fetchAndSetArea();
    });

    this.currentLotLayer = layer;
    this.addCurrentLotLabel();
    this.setPolygonCoordinates(layer);
    this.fetchAndSetArea();
  }

  handlePolygonRemove(event) {
    if (this.isProgrammaticRemoval) {
      return;
    }

    if (event.layer && event.layer.isExistingLot) {
      if (event.cancel && typeof event.cancel === "function") {
        event.cancel();
      }
      return false;
    }

    this.drawOverlayTarget.classList.remove("hidden");
    this.polygonCoordinatesTarget.value = "";
    this.geographicFileInputTarget.value = "";
    this.removeCenterDrawButton();
    this.isInDrawMode = false;
  }

  // Utility methods
  setPolygonCoordinates(layer = this.currentLotLayer) {
    const geometry = serializeLayerGeometry(layer);
    this.polygonCoordinatesTarget.value = geometry
      ? JSON.stringify(geometry)
      : "";
  }

  async fetchAndSetArea() {
    const polygon = this.polygonCoordinatesTarget.value;
    if (!polygon) return;

    const computeUrl = this.element.dataset.computeAreaUrl;
    if (!computeUrl) return;

    try {
      const response = await fetch(computeUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "text/vnd.turbo-stream.html",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')
            ?.content,
        },
        body: JSON.stringify({ polygon }),
      });

      if (response.ok) {
        await Turbo.renderStreamMessage(await response.text());
      }
    } catch (error) {
      console.error("Error computing area:", error);
    }
  }

  async handleFileUpload(event) {
    const file = event.target.files[0];

    if (file) {
      const fileName = file.name.toLowerCase();
      const isKmz = fileName.endsWith(".kmz");
      const isKml = fileName.endsWith(".kml");
      const isZip = fileName.endsWith(".zip");
      let geojson = null;

      if (isKmz || isKml || isZip) {
        geojson = await this.parseFileOnServer(file);
      } else {
        const content = await file.text();
        try {
          geojson = JSON.parse(content);
        } catch (error) {
          console.error("Error parsing GeoJSON:", error);
          return;
        }
      }

      if (geojson) {
        this.displayParsedGeometry(geojson);
      }
    }
  }

  async parseFileOnServer(file) {
    const parseUrl = this.geographicFileInputTarget.dataset.parseUrl;
    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await fetch(parseUrl, {
        method: "POST",
        body: formData,
        headers: {
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')
            ?.content,
          Accept: "application/json",
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error("Error parsing file:", errorData.error);
        alert(errorData.error || "Error parsing file");
        return null;
      }

      const data = await response.json();

      if (data.bulk) {
        this.showBulkImportNotice(data.count);
        return null;
      }

      return data.geojson;
    } catch (error) {
      console.error("Error uploading file:", error);
      alert("Error uploading file");
      return null;
    }
  }

  showBulkImportNotice(count) {
    const bulkImportUrl = this.geographicFileInputTarget.dataset.bulkImportUrl;
    if (!bulkImportUrl) return;
    const message =
      count === 1
        ? this.geographicFileInputTarget.dataset.bulkNoticeMessageOne ||
          "This file contains 1 lot."
        : this.geographicFileInputTarget.dataset.bulkNoticeMessageOther?.replace(
            "%{count}",
            count,
          ) || `This file contains ${count} lots.`;
    const importLabel =
      this.geographicFileInputTarget.dataset.bulkImportLabel || "Import all";

    const existingNotice = this.element.querySelector("[data-bulk-notice]");
    if (existingNotice) existingNotice.remove();

    const notice = document.createElement("div");
    notice.setAttribute("data-bulk-notice", "");
    notice.className =
      "flex gap-2 items-center px-3 py-2 mt-3 text-sm rounded-lg border border-info/20 bg-info/10";

    const icon = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    icon.setAttribute("fill", "none");
    icon.setAttribute("viewBox", "0 0 24 24");
    icon.setAttribute("class", "h-4 w-4 shrink-0 stroke-info");

    const iconPath = document.createElementNS(
      "http://www.w3.org/2000/svg",
      "path",
    );
    iconPath.setAttribute("stroke-linecap", "round");
    iconPath.setAttribute("stroke-linejoin", "round");
    iconPath.setAttribute("stroke-width", "2");
    iconPath.setAttribute(
      "d",
      "M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z",
    );
    icon.append(iconPath);

    const messageElement = document.createElement("span");
    messageElement.className = "min-w-0 grow text-base-content/70";
    messageElement.textContent = message;

    const importLink = document.createElement("a");
    importLink.href = bulkImportUrl;
    importLink.className =
      "font-medium whitespace-nowrap link link-primary shrink-0";
    importLink.textContent = `${importLabel} \u2192`;

    notice.append(icon, messageElement, importLink);
    this.geographicFileInputTarget.closest(".fieldset")?.after(notice);
  }

  displayParsedGeometry(geojson) {
    this.polygonCoordinatesTarget.value = JSON.stringify(geojson);

    this.removeCurrentLot();
    this.removeCenterDrawButton();
    this.isInDrawMode = false;
    this.drawOverlayTarget.classList.add("hidden");

    this.currentLotLayer = L.geoJSON(geojson, {
      style: this.constructor.CURRENT_LOT_STYLE,
      pmIgnore: false,
    }).addTo(this.map);

    this.markAsCurrentLot(this.currentLotLayer);
    this.addCurrentLotLabel();
    this.focusMap();
    this.fetchAndSetArea();
  }
}
